import React, { useEffect, useState, type RefObject } from 'react';

import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react';
import 'swiper/css/pagination';
import { Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { PaymentOption } from '../../../helpers/utils/types';
import CardDesign from './Components/CardDesign';

interface CardCarousalProps {
  paymentOptions: PaymentOption[];
  /** Used when `layout` is `swiper` (default). */
  onActiveIndexChange?: (swiper: any) => void;
  tenantBuildingId?: string;
  editMode?: boolean;
  paymentType?: string;
  /**
   * `scroll` — horizontal strip with snap (Pay Rent payment step style). Keeps `CardDesign` visuals.
   * `swiper` — existing Swiper carousel.
   */
  layout?: 'swiper' | 'scroll';
  /** Required when `layout` is `scroll`. */
  scrollRef?: RefObject<HTMLDivElement>;
  selectedOptionId?: string;
  onSelectOption?: (id: string) => void;
  onAddPaymentMethod?: () => void;
}

const ScrollStripCarousel = ({
  scrollRef,
  paymentOptions,
  selectedOptionId,
  onSelectOption,
  onAddPaymentMethod,
  editMode,
  paymentType,
}: {
  scrollRef: RefObject<HTMLDivElement>;
  paymentOptions: PaymentOption[];
  selectedOptionId: string;
  onSelectOption: (id: string) => void;
  onAddPaymentMethod: () => void;
  editMode?: boolean;
  paymentType?: string;
}) => {
  useEffect(() => {
    if (!scrollRef.current || !selectedOptionId) return;
    const root = scrollRef.current;
    const el = root.querySelector(`[data-payment-card="${selectedOptionId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [scrollRef, selectedOptionId, paymentOptions]);

  return (
    <div
      ref={scrollRef}
      className='flex items-start gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory py-2 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
    >
      {paymentOptions.map((option, index) => (
        <div
          key={option.paymentOptionId || option.id || `payment-option-${index}`}
          data-payment-card={option.id}
          className='snap-start flex-shrink-0 cursor-pointer'
          onClick={() => onSelectOption(option.id)}
        >
          <CardDesign
            cardBrand={option.cardBrand || undefined}
            cardEndingDigits={option.cardEndingDigits || undefined}
            cardExpiryDate={
              option.expiryMonth && option.expiryYear
                ? `${option.expiryMonth}/${option.expiryYear}`
                : undefined
            }
            isActive={option.id === selectedOptionId}
            paymentMethod={option.paymentMethod}
            isDefault={option.isDefault}
            editMode={editMode}
            percentageRate={option.percentageRate}
            cardCategoryName={option.cardCategoryName || ''}
            status={option.status}
            bankAccountName={option.bankAccountName}
            bankName={option.bankName}
            bankAccountNumber={option.bankAccountNumber}
            isBankAccountLinked={option.isBankAccountLinked}
            fixedProcessingFee={option.fixedProcessingFee}
            fees={option.fees}
            paymentType={paymentType}
          />
        </div>
      ))}

      <div className='snap-start flex-shrink-0'>
        <button
          type='button'
          onClick={onAddPaymentMethod}
          className='w-[min(300px,calc(100vw-88px))] xs:w-[300px] aspect-[268/155] rounded-[16px] sm:rounded-[20px] border-2 border-dashed border-[#D9DCE5] flex flex-col items-center justify-center gap-2 hover:border-[#0055FF] hover:bg-[#EEF5FF] transition-[colors,background-color,border-color] duration-200 ease-out group'
        >
          <span className='w-10 h-10 rounded-full bg-[#EEF5FF] group-hover:bg-[#CCE0FF] flex items-center justify-center text-[#0055FF] font-light text-[22px] transition-colors'>
            +
          </span>
          <span className='text-[13px] font-semibold text-[#6B7285] group-hover:text-[#0055FF] transition-colors px-2 text-center'>
            Add payment method
          </span>
        </button>
      </div>
    </div>
  );
};

const CardCarousal: React.FC<CardCarousalProps> = ({
  paymentOptions,
  onActiveIndexChange,
  editMode,
  paymentType,
  layout = 'swiper',
  scrollRef,
  selectedOptionId = '',
  onSelectOption,
  onAddPaymentMethod,
}) => {
  const [numberOfCardsToShow, setNumberOfCardsToShow] = useState<3 | 1 | 2>(3);
  const [direction, setDirection] = useState<'vertical' | 'horizontal'>('vertical'); // Default direction

  const handleResize = () => {
    if (paymentOptions.length === 1) {
      setNumberOfCardsToShow(1);
      setDirection('vertical');
      return;
    }
    if (window.innerHeight < 900) {
      // Height is less than 900
      if (window.innerWidth < 500) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else if (window.innerWidth < 700) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else if (window.innerWidth < 800) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else {
        // For width >= 1000
        setNumberOfCardsToShow(2);
        setDirection('vertical');
      }
    } else {
      // Height is greater than or equal to 900
      if (window.innerWidth < 700) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else if (window.innerWidth < 800) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else if (window.innerWidth < 1000) {
        setNumberOfCardsToShow(1);
        setDirection('horizontal');
      } else {
        // For width >= 1000
        setNumberOfCardsToShow(3);
        setDirection('vertical');
      }
    }
  };

  useEffect(() => {
    if (layout === 'scroll') return;
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [layout, paymentOptions.length]);

  if (layout === 'scroll' && scrollRef && onSelectOption && onAddPaymentMethod) {
    return (
      <ScrollStripCarousel
        scrollRef={scrollRef}
        paymentOptions={paymentOptions}
        selectedOptionId={selectedOptionId}
        onSelectOption={onSelectOption}
        onAddPaymentMethod={onAddPaymentMethod}
        editMode={editMode}
        paymentType={paymentType}
      />
    );
  }

  return (
    <>
      <div>
        <Swiper
          direction={direction}
          grabCursor={true}
          mousewheel={true}
          keyboard={true}
          slidesPerView={numberOfCardsToShow}
          centerInsufficientSlides={true}
          spaceBetween={48}
          pagination={{
            clickable: true,
          }}
          slideToClickedSlide={true}
          modules={[Pagination, Mousewheel, Keyboard]}
          onActiveIndexChange={onActiveIndexChange ?? (() => { })}
          style={{
            width: '100%',

            marginRight: 'auto',
            flex: 2,
          }}
        >
          {/* Conditionally render payment buttons inside SwiperSlide */}
          {paymentOptions ? (
            paymentOptions.map((option, index) => (
              <SwiperSlide key={option.paymentOptionId || option.id || `payment-option-${index}`}>
                <SlideWithActiveState>
                  {({ isActive }) => (
                    <div className='mt-5'>
                      <CardDesign
                        cardBrand={option.cardBrand || undefined}
                        cardEndingDigits={option.cardEndingDigits || undefined}
                        cardExpiryDate={
                          option.expiryMonth && option.expiryYear
                            ? `${option.expiryMonth}/${option.expiryYear}`
                            : undefined
                        }
                        isActive={isActive} // Pass the isActive state to CardDesign
                        paymentMethod={option.paymentMethod}
                        isDefault={option.isDefault}
                        editMode={editMode} // Pass isDefault state to CardDesign
                        percentageRate={option.percentageRate}
                        cardCategoryName={option.cardCategoryName || ''}
                        status={option.status}
                        bankAccountName={option.bankAccountName}
                        bankName={option.bankName}
                        bankAccountNumber={option.bankAccountNumber}
                        isBankAccountLinked={option.isBankAccountLinked}
                        fixedProcessingFee={option.fixedProcessingFee}
                        fees={option.fees}
                        paymentType={paymentType}
                      />
                    </div>
                  )}
                </SlideWithActiveState>
              </SwiperSlide>
            ))
          ) : (
            <SwiperSlide>
              <div className='payment-methods'>
                <p>Loading...</p>
              </div>
            </SwiperSlide>
          )}
          {numberOfCardsToShow > 1 && paymentOptions.length >= 2 && (
            <>
              <SwiperSlide key={`spacer-1`}>
                <div className='payment-methods'></div>
              </SwiperSlide>
              {numberOfCardsToShow >= 2 && (
                <SwiperSlide key={`spacer-2`}>
                  <div className='payment-methods'></div>
                </SwiperSlide>
              )}
            </>
          )}
        </Swiper>
        {paymentOptions.length > 2 && (
          <Swiper
            style={{
              marginTop: '2rem',
              width: '100%', // Use 100% width of the container
              // Limit to a maximum width if needed

              flex: 2,
            }}
          ></Swiper>
        )}
      </div>
    </>
  );
};

type SlideData = ReturnType<typeof useSwiperSlide>;

const SlideWithActiveState: React.FC<{ children: (data: SlideData) => React.ReactNode }> = ({
  children,
}) => {
  const slideData = useSwiperSlide();
  return <>{children(slideData)}</>; // Render children with slide data
};

export default CardCarousal;
